<template>
  <router-link :to="`/tool/${tool.slug}`" class="tool-card" @click.native="recordUse">
    <button
      :class="['card-fav', { active: isFav }]"
      @click.prevent.stop="toggleFav"
      :title="isFav ? $t('view.card.unfav') : $t('view.card.fav')"
    >
      <i :class="isFav ? 'el-icon-star-on' : 'el-icon-star-off'" />
    </button>
    <div class="card-icon-wrap" :style="{ background: iconBg }">
      <i :class="iconClass" :style="{ color: iconColor }" />
    </div>
    <div class="card-body">
      <h4 class="card-name">{{ tool.name }}</h4>
      <p class="card-desc">{{ tool.description || $t('view.common.noDescription') }}</p>
    </div>
    <div class="card-tags" v-if="displayTags.length">
      <span v-for="tag in displayTags" :key="tag" class="tag-chip" :class="tagClass(tag)">{{ tag }}</span>
    </div>
    <div class="card-footer">
      <span class="footer-cat"><i class="el-icon-folder-opened" /> {{ tool.categoryName || $t('view.common.tool') }}</span>
      <span class="footer-usage"><i class="el-icon-view" /> {{ formatCount(tool.useCount) }}</span>
      <span class="footer-fav"><i class="el-icon-star-on" /> {{ formatCount(tool.favCount) }}</span>
    </div>
  </router-link>
</template>

<script>
import { recordUsage } from '@/api/site'

const defaultIcons = ['el-icon-s-platform','el-icon-s-tools','el-icon-s-data','el-icon-s-cooperation','el-icon-s-order','el-icon-s-marketing']
const defaultColors = ['#f0f4ff','#f0fdf4','#fef3f2','#fdf2f8','#fef9f0','#f0f9ff']

// 组件类型 → 图标 + 背景色 + 图标色
const typeStyle = {
  // === 文本处理 ===
  'json-editor':     {icon:'el-icon-s-data',    bg:'#e8f0fe',color:'#4e7cff'},
  'text-editor':     {icon:'el-icon-document',  bg:'#f0f4ff',color:'#4e7cff'},
  'code-editor':     {icon:'el-icon-s-code',    bg:'#e8fde8',color:'#43e97b'},
  'diff-viewer':     {icon:'el-icon-sort',      bg:'#f0f4ff',color:'#4e7cff'},
  'xml-formatter':   {icon:'el-icon-s-flag',    bg:'#f0f4ff',color:'#4e7cff'},
  'sql-formatter':   {icon:'el-icon-s-data',    bg:'#e8f0fe',color:'#4e7cff'},
  'markdown-editor': {icon:'el-icon-edit-outline',bg:'#f0fdf4',color:'#43e97b'},
  'char-count':      {icon:'el-icon-s-order',   bg:'#f0f4ff',color:'#4e7cff'},
  'text-dedup':      {icon:'el-icon-rank',      bg:'#f0f4ff',color:'#4e7cff'},
  'case-converter':  {icon:'el-icon-sort',      bg:'#f5f0ff',color:'#6b3cf0'},
  'chinese-converter':{icon:'el-icon-sort',     bg:'#fdf3e8',color:'#e6a23c'},
  'pinyin-converter':{icon:'el-icon-reading',   bg:'#e8f0fe',color:'#4e7cff'},
  'ascii-art':       {icon:'el-icon-picture-outline-round',bg:'#1a1f36',color:'#43e97b'},

  // === 编码解码 ===
  'converter':       {icon:'el-icon-sort',      bg:'#fef3f2',color:'#f56c6c'},
  'url-codec':       {icon:'el-icon-share',     bg:'#fef3f2',color:'#f56c6c'},
  'base64-codec':    {icon:'el-icon-sort',      bg:'#fef3f2',color:'#f56c6c'},
  'crypto-tool':     {icon:'el-icon-lock',      bg:'#e8fde8',color:'#43e97b'},
  'jwt-decoder':     {icon:'el-icon-lock',      bg:'#e8f0fe',color:'#4e7cff'},
  'html-entity':     {icon:'el-icon-video-play',bg:'#f0fdf4',color:'#67c23a'},
  'yaml-json':       {icon:'el-icon-sort',      bg:'#f5f0ff',color:'#6b3cf0'},
  'yaml-properties': {icon:'el-icon-sort',      bg:'#fef3e8',color:'#e6a23c'},
  'base-converter':  {icon:'el-icon-s-operation',bg:'#e8f0fe',color:'#4e7cff'},

  // === 开发工具 ===
  'regex-tester':    {icon:'el-icon-search',    bg:'#f0f4ff',color:'#4e7cff'},
  'timestamp-tool':  {icon:'el-icon-time',      bg:'#f0f4ff',color:'#4e7cff'},
  'uuid-generator':  {icon:'el-icon-magic-stick',bg:'#fef9f0',color:'#e6a23c'},
  'generator':       {icon:'el-icon-magic-stick',bg:'#fef9f0',color:'#e6a23c'},
  'password-strength':{icon:'el-icon-lock',     bg:'#fef3e8',color:'#e6a23c'},
  'http-status':     {icon:'el-icon-circle-check',bg:'#e8fde8',color:'#43e97b'},
  'api-tester':      {icon:'el-icon-connection',bg:'#e8f0fe',color:'#4e7cff'},
  'sql-query':       {icon:'el-icon-s-data',    bg:'#e8f0fe',color:'#4e7cff'},
  'redis-query':     {icon:'el-icon-connection',bg:'#fef3f2',color:'#f56c6c'},
  'dns-lookup':      {icon:'el-icon-connection',bg:'#e8f0fe',color:'#4e7cff'},
  'websocket-tester':{icon:'el-icon-connection',bg:'#e8fde8',color:'#67c23a'},
  'ssl-checker':     {icon:'el-icon-lock',      bg:'#f0fdf4',color:'#43e97b'},
  'coord-converter': {icon:'el-icon-location-outline',bg:'#e8f0fe',color:'#4e7cff'},
  'cron-generator':  {icon:'el-icon-time',      bg:'#f5f0ff',color:'#6b3cf0'},
  'table-structure': {icon:'el-icon-tickets',   bg:'#f0f4ff',color:'#4e7cff'},
  'swagger-doc':     {icon:'el-icon-document',  bg:'#f5f0ff',color:'#6b3cf0'},

  // === 作图工具 ===
  'qrcode-generator':{icon:'el-icon-s-grid',    bg:'#1a1f36',color:'#fff'},
  'qr-decoder':      {icon:'el-icon-s-grid',    bg:'#1a1f36',color:'#fff'},
  'barcode-generator':{icon:'el-icon-s-grid',   bg:'#e8f0fe',color:'#4e7cff'},
  'color-picker':    {icon:'el-icon-brush',     bg:'#fdf2f8',color:'#e040fb'},
  'color-extractor': {icon:'el-icon-aim',       bg:'#fdf2f8',color:'#e040fb'},
  'icon-generator':  {icon:'el-icon-star-on',   bg:'#fef9f0',color:'#e6a23c'},
  'function-plot':   {icon:'el-icon-s-data',    bg:'#e8f0fe',color:'#4e7cff'},
  'flowchart':       {icon:'el-icon-s-data',    bg:'#f0f4ff',color:'#4e7cff'},
  'mindmap':         {icon:'el-icon-share',     bg:'#fef9f0',color:'#e6a23c'},
  'sequence-diagram':{icon:'el-icon-sort',      bg:'#f0f4ff',color:'#4e7cff'},
  'chart-maker':     {icon:'el-icon-s-data',    bg:'#e8f0fe',color:'#4e7cff'},
  'image-bg-remove': {icon:'el-icon-picture-outline',bg:'#fdf2f8',color:'#e040fb'},
  'image-compress':  {icon:'el-icon-picture-outline',bg:'#f0fdf4',color:'#67c23a'},
  'image-ocr':       {icon:'el-icon-reading',   bg:'#f0f4ff',color:'#4e7cff'},
  'watermark-remove':{icon:'el-icon-delete-solid',bg:'#fef0f0',color:'#f56c6c'},

  // === 文档转换 ===
  'word-to-html':    {icon:'el-icon-document-copy',bg:'#e8f0fe',color:'#4e7cff'},
  'html-to-word':    {icon:'el-icon-document-add',bg:'#e8f0fe',color:'#4e7cff'},
  'word-to-pdf':     {icon:'el-icon-tickets',   bg:'#fef3f2',color:'#f56c6c'},
  'pdf-to-word':     {icon:'el-icon-tickets',   bg:'#f0fdf4',color:'#67c23a'},
  'ppt-to-word':     {icon:'el-icon-tickets',   bg:'#fde8ef',color:'#e040fb'},
  'excel-to-word':   {icon:'el-icon-tickets',   bg:'#e8fef0',color:'#43a047'},
  'pdf-split':       {icon:'el-icon-tickets',   bg:'#fef3e8',color:'#e6a23c'},

  // === 计算助手 ===
  'calculator':      {icon:'el-icon-s-operation',bg:'#e8f0fe',color:'#4e7cff'},
  'mortgage-calculator':{icon:'el-icon-s-marketing',bg:'#f0f4ff',color:'#4e7cff'},
  'tax-calculator':  {icon:'el-icon-s-marketing',bg:'#fdf3e8',color:'#e6a23c'},
  'bmi-calculator':  {icon:'el-icon-s-operation',bg:'#e8f0fe',color:'#4e7cff'},
  'baby-height':     {icon:'el-icon-s-operation',bg:'#fde8f0',color:'#f56c6c'},
  'compound-interest':{icon:'el-icon-s-marketing',bg:'#f0fdf4',color:'#43e97b'},
  'unit-converter':  {icon:'el-icon-sort',      bg:'#f0f4ff',color:'#4e7cff'},
  'mbti-test':       {icon:'el-icon-user-solid',bg:'#fef3e8',color:'#e6a23c'},
  'blood-type':      {icon:'el-icon-help',      bg:'#fef0f0',color:'#f56c6c'},

  // === 实用查询 ===
  'phone-list':      {icon:'el-icon-phone-outline',bg:'#e8fde8',color:'#43e97b'},
  'location-query':  {icon:'el-icon-location-outline',bg:'#fef3f2',color:'#f56c6c'},
  'express-tracking':{icon:'el-icon-truck',     bg:'#e8f0fe',color:'#4e7cff'},
  'timezone-converter':{icon:'el-icon-time',    bg:'#f0f4ff',color:'#4e7cff'},
}
const tagPalette = ['tag-blue', 'tag-green', 'tag-amber', 'tag-purple', 'tag-teal', 'tag-rose', 'tag-indigo', 'tag-lime']

export default {
  props: { tool: { type: Object, required: true } },
  computed: {
    isFav() { return this.tool.id && this.$store.getters.isFav(this.tool.id) },
    iconClass() {
      const s = typeStyle[this.tool.componentType]
      return s ? s.icon : defaultIcons[(this.tool.id||0) % defaultIcons.length]
    },
    iconBg() {
      const s = typeStyle[this.tool.componentType]
      return s ? s.bg : defaultColors[(this.tool.id||0) % defaultColors.length]
    },
    iconColor() {
      const s = typeStyle[this.tool.componentType]
      return s ? s.color : '#4e7cff'
    },
    displayTags() {
      const tags = this.tool.tags || []
      return tags.slice(0, 4)
    }
  },
  methods: {
    toggleFav() {
      const isFav = this.$store.getters.isFav(this.tool.id)
      // 本地即时更新收藏数
      if (isFav) {
        this.tool.favCount = Math.max((this.tool.favCount || 0) - 1, 0)
      } else {
        this.tool.favCount = (this.tool.favCount || 0) + 1
      }
      this.$store.dispatch('toggleFavAction', this.tool)
    },
    recordUse() { recordUsage(this.tool.id).catch(() => {}) },
    formatCount(n) {
      if (!n) return 0
      if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n
    },
    tagClass(tag) {
      let hash = 0
      for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash)
      return tagPalette[Math.abs(hash) % tagPalette.length]
    }
  }
}
</script>

<style scoped>
.tool-card {
  display: flex; flex-direction: column;
  background: #fff; border-radius: 12px;
  padding: 24px 20px 16px;
  transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
  border: 1px solid #ebeef5;
  height: 100%; min-height: 200px; box-sizing: border-box;
  position: relative;
}
.card-fav {
  position: absolute; top: 12px; right: 12px;
  width: 32px; height: 32px; border-radius: 8px;
  border: none; background: transparent;
  font-size: 16px; cursor: pointer; color: #c0c4cc;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; z-index: 2;
}
.card-fav:hover { color: #e6a23c; background: rgba(230,162,60,0.08); }
.card-fav.active { color: #e6a23c; }
.tool-card:hover {
  box-shadow: 0 8px 24px rgba(78,124,255,0.12);
  border-color: #4e7cff; transform: translateY(-4px);
}

/* icon */
.card-icon-wrap {
  width: 48px; height: 48px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px; flex-shrink: 0;
}
.card-icon-wrap i { font-size: 22px; color: #4e7cff; }

/* body */
.card-body { flex: 0 0 auto; min-width: 0; margin-bottom: 10px; }
.card-name {
  margin: 0 0 6px; font-size: 16px; font-weight: 700; color: #1a1f36;
  line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
}
.card-desc {
  margin: 0; font-size: 13px; color: #8892a6; line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* tags */
.card-tags { display: flex; flex-wrap: wrap; gap: 6px; flex: 1; align-content: flex-start; }
.tag-chip {
  font-size: 11px; padding: 2px 8px; border-radius: 4px;
  font-weight: 500; letter-spacing: 0.5px;
  white-space: nowrap;
}
.tag-blue   { color: #3b5ccc; background: #e8edff; }
.tag-green  { color: #2d8a56; background: #e8faf0; }
.tag-amber  { color: #b85a1a; background: #fef6e8; }
.tag-purple { color: #7c3aed; background: #f3eeff; }
.tag-teal   { color: #0d7a6e; background: #e6f9f6; }
.tag-rose   { color: #be254d; background: #fde8ef; }
.tag-indigo { color: #4338ca; background: #e8ecff; }
.tag-lime   { color: #5c7a29; background: #f4fae6; }

/* footer */
.card-footer {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 12px; padding-top: 10px;
  border-top: 1px solid #f2f3f5; flex-shrink: 0;
}
.footer-cat { font-size: 12px; color: #909399; display: flex; align-items: center; gap: 3px; }
.footer-usage { font-size: 12px; color: #c0c4cc; display: flex; align-items: center; gap: 3px; }
.footer-fav { font-size: 12px; color: #e6a23c; display: flex; align-items: center; gap: 3px; }
</style>
